import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Reorder } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_META } from "@/lib/constants";
import { Plus, Folder, FolderOpen, Trash2, MoreVertical, Pencil, Building, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GlobalSearch from "@/components/GlobalSearch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function BuilderRow({ b, canManage, category, navigate, setEditing, setEditName, removeBuilder }) {
  return (
    <>
      {canManage && <GripVertical className="h-4 w-4 text-zinc-300 cursor-grab active:cursor-grabbing shrink-0" />}
      <div className="h-10 w-10 bg-zinc-900 text-white flex items-center justify-center rounded-sm shrink-0">
        <Folder className="h-5 w-5" />
      </div>
      <button className="flex-1 min-w-0 text-left" onClick={() => navigate(`/folder/${category}/builder/${b.builder_id}`)}
        data-testid={`builder-card-${b.builder_id}`}>
        <h3 className="font-heading font-bold text-base leading-snug truncate">{b.name}</h3>
        <p className="text-xs text-zinc-500 mt-0.5">{b.project_count} project{b.project_count !== 1 ? "s" : ""}</p>
      </button>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-zinc-400 hover:text-zinc-900 transition-colors shrink-0" data-testid={`builder-menu-${b.builder_id}`}>
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm">
            <DropdownMenuItem onClick={() => { setEditing(b); setEditName(b.name); }}>
              <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm text-red-600 hover:bg-red-50" data-testid={`delete-builder-${b.builder_id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete folder
                </div>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete &ldquo;{b.name}&rdquo;?</AlertDialogTitle>
                  <AlertDialogDescription>Its projects will be moved to Ungrouped, not deleted.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => removeBuilder(b.builder_id)}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

export default function FolderView() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.can_manage;
  const meta = CATEGORY_META[category];

  const [builders, setBuilders] = useState([]);
  const [ungrouped, setUngrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const buildersRef = useRef([]);
  useEffect(() => { buildersRef.current = builders; }, [builders]);

  const load = useCallback(async () => {
    setLoading(true);
    const [b, u] = await Promise.all([
      api.get("/builders", { params: { category } }),
      api.get("/projects", { params: { category, builder_id: "none" } }),
    ]);
    setBuilders(b.data);
    setUngrouped(u.data);
    setLoading(false);
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const persistOrder = async () => {
    try { await api.post("/builders/reorder", { ordered_ids: buildersRef.current.map((b) => b.builder_id) }); }
    catch (e) { toast.error("Failed to save order"); }
  };

  const createBuilder = async () => {
    if (!name.trim()) { toast.error("Folder name is required"); return; }
    setSaving(true);
    try {
      await api.post("/builders", { name, category });
      toast.success("Folder created");
      setName(""); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e, "Failed to create folder")); }
    finally { setSaving(false); }
  };

  const rename = async () => {
    if (!editName.trim()) return;
    try {
      await api.patch(`/builders/${editing.builder_id}`, { name: editName });
      toast.success("Folder renamed");
      setEditing(null); load();
    } catch (e) { toast.error("Failed to rename"); }
  };

  const removeBuilder = async (id) => {
    try {
      await api.delete(`/builders/${id}`);
      toast.success("Folder deleted — projects moved to Ungrouped");
      load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  if (!meta) return <div className="p-10">Unknown folder</div>;
  const Icon = meta.icon;
  const rowProps = { canManage, category, navigate, setEditing, setEditName, removeBuilder };

  return (
    <div className="p-6 lg:p-10 max-w-[900px]">
      <header className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 text-zinc-500 mb-2">
            <Icon className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">{meta.sub}</span>
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tighter">{meta.label}</h1>
          <p className="text-zinc-500 mt-2 text-sm">{builders.length} builder folder{builders.length !== 1 ? "s" : ""}{canManage && builders.length > 1 ? " · drag to reorder" : ""}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
        {(builders.length > 0 || ungrouped.length > 0) && (
          <Select onValueChange={(v) => navigate(`/folder/${category}/builder/${v}`)}>
            <SelectTrigger className="h-10 w-auto min-w-[190px] gap-2 rounded-sm border-zinc-200 text-sm" data-testid="builder-filter">
              <SelectValue placeholder="Jump to builder…" />
            </SelectTrigger>
            <SelectContent>
              {builders.map((b) => (
                <SelectItem key={b.builder_id} value={b.builder_id} data-testid={`builder-filter-${b.builder_id}`}>
                  {b.name} <span className="text-zinc-400">· {b.project_count}</span>
                </SelectItem>
              ))}
              {ungrouped.length > 0 && (
                <SelectItem value="none" data-testid="builder-filter-none">
                  Ungrouped <span className="text-zinc-400">· {ungrouped.length}</span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
        <GlobalSearch category={category} />
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-builder-button" className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2">
                <Plus className="h-4 w-4" /> New Builder Folder
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-heading">New Builder Folder</DialogTitle></DialogHeader>
              <div className="py-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Folder Name</label>
                <Input data-testid="builder-name-input" value={name} className="mt-1.5 rounded-sm"
                  placeholder="e.g. 02 Lusso Homes" onChange={(e) => setName(e.target.value)} />
              </div>
              <DialogFooter>
                <Button data-testid="save-builder-button" onClick={createBuilder} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm">
                  {saving ? "Creating…" : "Create Folder"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </header>

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : builders.length === 0 && ungrouped.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center">
          <Building className="h-8 w-8 text-zinc-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold mb-2">No builder folders yet</h3>
          <p className="text-zinc-500 text-sm max-w-sm mx-auto">
            {canManage ? "Create a builder folder (e.g. Lusso Homes) to group projects by builder." : "No folders have been created yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {canManage ? (
            <Reorder.Group axis="y" values={builders} onReorder={setBuilders} className="space-y-3">
              {builders.map((b) => (
                <Reorder.Item key={b.builder_id} value={b} onDragEnd={persistOrder}
                  className="bg-white border border-zinc-200 p-4 flex items-center gap-3 hover:border-zinc-300 transition-colors">
                  <BuilderRow b={b} {...rowProps} />
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            builders.map((b) => (
              <div key={b.builder_id} className="bg-white border border-zinc-200 p-4 flex items-center gap-3">
                <BuilderRow b={b} {...rowProps} />
              </div>
            ))
          )}

          {ungrouped.length > 0 && (
            <button
              className="w-full bg-white border border-dashed border-zinc-300 p-4 flex items-center gap-3 hover:border-zinc-400 transition-colors text-left"
              onClick={() => navigate(`/folder/${category}/builder/none`)}
              data-testid="builder-card-none">
              <div className="h-10 w-10 bg-zinc-100 text-zinc-500 flex items-center justify-center rounded-sm shrink-0">
                <FolderOpen className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-base leading-snug">Ungrouped</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{ungrouped.length} project{ungrouped.length !== 1 ? "s" : ""}</p>
              </div>
            </button>
          )}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">Rename Folder</DialogTitle></DialogHeader>
          <Input value={editName} className="rounded-sm" onChange={(e) => setEditName(e.target.value)} data-testid="rename-builder-input" />
          <DialogFooter>
            <Button onClick={rename} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="save-rename-builder">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
